import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { mediaService } from '../../services/api';
import PhotoSequenceEditor from './PhotoSequenceEditor';

interface ImageGalleryWithCoverProps {
  images: string[];
  coverImage?: string;
  onImagesChange: (images: string[]) => void;
  onCoverImageChange: (coverImage: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

const UploadButton = styled.label`
  aspect-ratio: 1;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #64748b;
  font-size: 0.875rem;

  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const InfoMessage = styled.p`
  margin-bottom: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #b91c1c;
  font-size: 0.875rem;
`;

const ImageGalleryWithCover: React.FC<ImageGalleryWithCoverProps> = ({
  images,
  coverImage,
  onImagesChange,
  onCoverImageChange,
  onUploadingChange
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const galleryRef = useRef({ images, coverImage });
  galleryRef.current = { images, coverImage };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const files = Array.from(input.files || []);
    if (isUploading || files.length === 0) return;

    setIsUploading(true);
    setUploadErrors([]);
    onUploadingChange?.(true);

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          setUploadErrors(errors => [...errors, `${file.name}: selecione um arquivo de imagem.`]);
          continue;
        }
        if (file.size > 5 * 1024 * 1024) {
          setUploadErrors(errors => [...errors, `${file.name}: o limite é de 5 MB por imagem.`]);
          continue;
        }

        try {
          const uploaded = await mediaService.uploadImage(file);
          const url = uploaded?.url || uploaded?.media?.url;
          if (typeof url !== 'string' || !url.startsWith('/uploads/')) {
            throw new Error('Resposta de upload sem URL de imagem persistida');
          }
          const current = galleryRef.current;
          const updatedImages = [...current.images, url];
          const updatedCover = current.coverImage || updatedImages[0];
          galleryRef.current = { images: updatedImages, coverImage: updatedCover };
          onImagesChange(updatedImages);
          if (!current.coverImage) onCoverImageChange(updatedCover);
        } catch {
          setUploadErrors(errors => [...errors, `${file.name}: não foi possível enviar. Tente novamente.`]);
        }
      }
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
      input.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    const removedImage = images[index];
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);

    // Se a imagem removida era a capa, definir a primeira imagem como nova capa
    if (removedImage === coverImage && newImages.length > 0) {
      onCoverImageChange(newImages[0]);
    } else if (newImages.length === 0) {
      onCoverImageChange('');
    }
  };

  return (
    <>
      <InfoMessage>
        Arraste pelo número (⠿) ou use as setas para mudar a sequência das fotos.
        {' '}Clique em "Definir capa" para escolher a imagem principal do imóvel.
      </InfoMessage>
      {uploadErrors.length > 0 && <ErrorMessage role="alert">{uploadErrors.join(' ')}</ErrorMessage>}

      <PhotoSequenceEditor
        images={images}
        coverImage={coverImage}
        onChange={onImagesChange}
        onSetCover={onCoverImageChange}
        onRemove={handleRemoveImage}
        footerSlot={
          <UploadButton>
            <UploadInput
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              disabled={isUploading}
            />
            <UploadIcon>+</UploadIcon>
            <span>{isUploading ? 'Carregando...' : 'Adicionar Imagens'}</span>
          </UploadButton>
        }
      />
    </>
  );
};

export default ImageGalleryWithCover;
